import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { login as apiLogin, logout as apiLogout, verifyAuth } from '../api/auth';
import { isLegacyViewAllowed } from '../config/legacyAccess';

const AuthContext = createContext(null);
const getShouldSkipAuthCheck = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const pathname = window.location.pathname || '';
  const searchParams = new URLSearchParams(window.location.search || '');
  const isPublicShellRoute = pathname === '/' ||
    pathname === '/landingpagedemo' ||
    pathname === '/login' ||
    pathname === '/forgot-password' ||
    pathname === '/reset-password' ||
    pathname === '/accept-invitation' ||
    pathname === '/brand-guidelines';

  return isPublicShellRoute ||
    pathname.startsWith('/public/') ||
    pathname.startsWith('/embed/') ||
    searchParams.get('orgIframe') === 'true' ||
    searchParams.has('token') ||
    searchParams.has('t');
};

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skipAuthCheck, setSkipAuthCheck] = useState(() => getShouldSkipAuthCheck());

  // Track if initial auth check has been done
  const initialAuthDoneRef = React.useRef(false);
  const skipAuthCheckRef = React.useRef(getShouldSkipAuthCheck());
  
  // Keep ref in sync without triggering effect
  React.useEffect(() => {
    skipAuthCheckRef.current = skipAuthCheck;
  }, [skipAuthCheck]);

  // Check if user is already logged in on component mount - ONLY RUNS ONCE
  useEffect(() => {
    // Prevent re-running if already done
    if (initialAuthDoneRef.current) {
      console.log('🔐 [AuthContext] Skipping duplicate auth effect run');
      return;
    }

    const shouldSkipInitialAuth = getShouldSkipAuthCheck();
    if (skipAuthCheckRef.current !== shouldSkipInitialAuth) {
      skipAuthCheckRef.current = shouldSkipInitialAuth;
    }
    if (skipAuthCheck !== shouldSkipInitialAuth) {
      setSkipAuthCheck(shouldSkipInitialAuth);
    }
    
    // Mark as done immediately to prevent race conditions in React strict mode or HMR
    initialAuthDoneRef.current = true;
    
    console.log('🔐 [AuthContext] Auth effect running (initial)', {
      skipAuthCheck: shouldSkipInitialAuth,
      pathname: typeof window !== 'undefined' ? window.location.pathname : '',
      search: typeof window !== 'undefined' ? window.location.search : '',
      timestamp: Date.now()
    });

    const checkAuthStatus = async () => {
      // Skip auth check if we're in the process of logging out
      if (skipAuthCheckRef.current) {
        console.log('🔐 [AuthContext] Skipping auth check (skipAuthCheck=true)');
        setLoading(false);
        return;
      }

      try {
        console.log('🔐 [AuthContext] Starting auth verification...');
        setLoading(true);
        const result = await verifyAuth();
        
        if (result.authenticated) {
          setUser(result.user);
        } else {
          // If verification returns false or has an error property
          setUser(null);
          if (result.error) {
            console.warn('⚠️ [AuthContext] Auth verification error:', result.error);
          }
        }
      } catch (err) {
        // This should rarely happen as verifyAuth handles errors internally
        console.error('💥 [AuthContext] Unexpected auth verification error:', err);
        setUser(null);
      } finally {
        setLoading(false);
        initialAuthDoneRef.current = true;
      }
    };

    // Always check auth status, even on public routes (to detect logged-in users)
    console.log('🔐 [AuthContext] Calling checkAuthStatus...');
    checkAuthStatus();
    
    // Set up interval to periodically check auth status
    const intervalId = setInterval(() => {
      if (!skipAuthCheckRef.current) {
        console.log('🔐 [AuthContext] Periodic auth check');
        checkAuthStatus();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    
    return () => {
      console.log('🔐 [AuthContext] Auth effect cleanup');
      clearInterval(intervalId);
    };
  }, [skipAuthCheck]); // Re-check the current URL-derived skip flag on initial mount

  // Login function
  const login = async (identifier, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiLogin(identifier, password);
      
      if (response.success) {
        setUser(response.user);
        return { success: true, user: response.user };
      } else {
        console.warn('Login failed:', response.message);
        setError(response.message || 'Login failed');
        return { success: false, error: response.message || 'Login failed' };
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed');
      return { success: false, error: err.message || 'Login failed' };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Set flag to skip auth check during logout
      setSkipAuthCheck(true);
      setLoading(true);
      
      // Clear user state immediately
      setUser(null);
      
      // Perform API logout
      await apiLogout();
    } catch (err) {
      console.error('Logout error:', err.message);
      // User state already cleared above
    } finally {
      setLoading(false);
      // Don't automatically reset skipAuthCheck - let components handle it
    }
  };

  // Memoized isAuthenticated function to prevent unnecessary re-renders
  const isAuthenticated = useCallback(() => {
    return !!user;
  }, [user]);

  // Memoized isAdmin function to prevent unnecessary re-renders
  const isAdmin = useCallback(() => {
    // If is_active is explicitly false, return false
    // Otherwise, if user exists, consider them admin
    const isActive = user?.is_active !== false;
    return isActive && !!user;
  }, [user]);
  
  // Refresh authentication status
  const refreshAuth = async () => {
    try {
      setLoading(true);
      const result = await verifyAuth();
      
      if (result.authenticated) {
        setUser(result.user);
        return true;
      } else {
        setUser(null);
        return false;
      }
    } catch (err) {
      console.error('Auth refresh error:', err);
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    isAdmin,
    refreshAuth,
    setError, // Expose setError to allow clearing errors
    skipAuthCheck, // Expose the skipAuthCheck flag
    setSkipAuthCheck, // Expose this to components that need to skip auth check
    setUser // Expose setUser for test authentication scenarios
  }), [user, loading, error, isAuthenticated, isAdmin, skipAuthCheck]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
